// Solo mode runs the exact same rules as the multiplayer DO tick loop (via
// ../game-sim), but entirely in the browser at animation-frame rate instead
// of over a WebSocket round-trip to a Durable Object — there's no opponent to
// stay in sync with, so paying DO/network latency for solo gameplay was pure
// overhead. The server is only involved once, to record the finished run on
// the leaderboard (see App.tsx's solo-finish POST).

import { createInitialEnemies, createInitialPlayer, stepEnemies, stepPlayerTick } from "../game-sim";
import { LEVELS } from "../level";

import type { EnemyState, PlayerSimState } from "../game-sim";
import type { LevelConfig } from "../level";
import type { EnemyTickState, GameInput, PlayerTickState } from "../shared";

export type SoloSnapshot = {
	elapsedMs: number;
	players: PlayerTickState[];
	enemies: EnemyTickState[];
	collectedIds: string[];
	powerUpTaken: boolean;
	finishOrder: string[];
	finishTimesMs: Record<string, number>;
};

export class SoloSim {
	private readonly playerId: string;
	private readonly startMs: number;
	private readonly level: LevelConfig;
	private player: PlayerSimState;
	private enemies: Map<string, EnemyState>;
	private collectiblesRemaining: Set<string>;
	private powerUpTaken = false;
	private collectedIds: string[] = [];
	private finishTimeMs: number | undefined;

	constructor(playerId: string, levelIndex = 0) {
		const now = Date.now();
		this.playerId = playerId;
		this.startMs = now;
		this.level = LEVELS[levelIndex] ?? LEVELS[0];
		this.player = createInitialPlayer(now, this.level);
		this.enemies = createInitialEnemies(this.level);
		this.collectiblesRemaining = new Set(this.level.collectibles.map((c) => c.id));
	}

	step(input: GameInput, dtSec: number): SoloSnapshot {
		const now = Date.now();
		if (!this.player.finished && !this.player.gameOver) {
			const elapsedSec = (now - this.startMs) / 1000;
			stepEnemies(this.enemies, this.level, elapsedSec, dtSec);
			const powerUp = { taken: this.powerUpTaken };
			const result = stepPlayerTick(
				this.player,
				input,
				this.enemies,
				this.collectiblesRemaining,
				powerUp,
				now,
				dtSec,
				this.level,
			);
			this.powerUpTaken = powerUp.taken;
			this.collectedIds.push(...result.collectedIds);
			if (result.finishTimeMs !== undefined) this.finishTimeMs = result.finishTimeMs;
		}
		return this.snapshot(now);
	}

	snapshot(now = Date.now()): SoloSnapshot {
		const p = this.player;
		return {
			elapsedMs: now - this.startMs,
			players: [
				{
					playerId: this.playerId,
					x: p.x,
					y: p.y,
					facing: p.facing,
					lives: p.lives,
					score: p.score,
					finished: p.finished,
					gameOver: p.gameOver,
					invincible: p.invincibleUntil > now,
				},
			],
			enemies: [...this.enemies.entries()].map(([id, e]) => ({ id, x: e.x, y: e.y, alive: e.alive })),
			collectedIds: [...this.collectedIds],
			powerUpTaken: this.powerUpTaken,
			finishOrder: this.player.finished ? [this.playerId] : [],
			finishTimesMs:
				this.player.finished && this.finishTimeMs !== undefined ? { [this.playerId]: this.finishTimeMs } : {},
		};
	}
}
